import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, Injector, input, OnInit, type Signal, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  BackupDTabFormStructure,
  FormKey,
  FormStructure
} from '@common/components/my-account/tabs/backup-destination-tab/backup-destination-tab.model';
import { AccessKeyTextPipe } from '@common/components/my-account/tabs/backup-destination-tab/help-pipes/access-key-text.pipe';
import { BucketNameTextPipe } from '@common/components/my-account/tabs/backup-destination-tab/help-pipes/bucket-name-text.pipe';
import { IsAvailableRegionPipe } from '@common/components/my-account/tabs/backup-destination-tab/help-pipes/is-available-region.pipe';
import { SecretKeyTextPipe } from '@common/components/my-account/tabs/backup-destination-tab/help-pipes/secret-key-text.pipe';
import { UiDataService } from '@common/components/my-account/tabs/backup-destination-tab/help-services/ui-data.service';
import { SECRET_KEY_MASK } from '@common/constants';
import { Domain, StorageAccount, StorageType } from '@common/models';
import { PriceToDisplayPipe } from '@common/pipes';
import { AbilityService } from '@common/services/auth/ability.service';
import { DomainEffectService } from '@common/services/domain/domain-effect.service';
import { showHelpButton } from '@common/utils';
import { I18NextPipe } from 'angular-i18next';
import { I18_NAMESPACE_APPS_UI } from 'i18n';
import { AlertComponent, FormGroupComponent, InputComponent, MbsPopupType, SelectComponent } from 'mbs-ui-kit';
import { distinctUntilChanged } from 'rxjs/operators';

const components = [AlertComponent, FormGroupComponent, InputComponent, SelectComponent];
const directives = [NgTemplateOutlet];
const modules = [ReactiveFormsModule];
const pipes = [AccessKeyTextPipe, BucketNameTextPipe, I18NextPipe, IsAvailableRegionPipe, PriceToDisplayPipe, SecretKeyTextPipe];

const helpBaseUrl = 'https://help.mspbackups.com/';

@Component({
  imports: [...components, ...directives, ...modules, ...pipes],
  selector: 'app-storage-settings-form',
  templateUrl: './storage-settings-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BackupDestinationComponent implements OnInit {
  private readonly ability = inject(AbilityService);
  private readonly domainEffect = inject(DomainEffectService);
  private readonly injector = inject(Injector);
  private readonly uiDataService = inject(UiDataService);

  protected readonly moduleAccount = I18_NAMESPACE_APPS_UI.account;
  protected readonly moduleButton = I18_NAMESPACE_APPS_UI.button;
  protected readonly FormKey = FormKey;
  protected readonly StorageType = StorageType;
  protected readonly MbsPopupType = MbsPopupType;
  protected readonly secretKeyIconClasses = {
    lock: 'fa fa-eye',
    unlock: 'fa fa-eye-slash'
  };

  public readonly formGroup = input.required<FormGroup<FormStructure>>();
  public readonly storages = input.required<StorageAccount[]>();
  public readonly initialSettings = input.required<BackupDTabFormStructure>();
  public readonly appendTo = input.required<string>();

  protected readonly readonly = signal<boolean>(false);
  protected readonly disabledSecretKey = signal<boolean>(false);

  protected storageTypeChanges!: Signal<StorageType | null>;
  protected readonly regions = computed(() => this.storages().find((s) => s.Name === this.storageTypeChanges())?.Regions ?? []);
  protected readonly priceGB = computed(() => this.storages().find((s) => s.Name === this.storageTypeChanges())?.CentPriceGbPerMonth ?? 0);
  protected readonly priceTB = computed(() => this.storages().find((s) => s.Name === this.storageTypeChanges())?.CentPriceTbPerMonth ?? 0);
  // protected readonly sizeBytes = computed(() => this.initialSettings()?.[FormKey.CurrentSizeBytes] ?? 0);

  protected readonly manageStorageLink = 'https://help.msp360.com/cloudberry-backup-for-ms-office-365/manage-storage';
  protected readonly storageLink = computed<string | null>(() => {
    switch (this.storageTypeChanges()) {
      case StorageType.AmazonS3UnifiedBilling:
        return helpBaseUrl + 'billing-storage/storage-providers/msp360-aws-storage';
      case StorageType.WasabiUnifiedBilling:
        return helpBaseUrl + 'billing-storage/storage-providers/msp360-storage';
      default:
        return null;
    }
  });

  protected readonly isAdmin = this.ability.isAdmin;

  protected readonly currentDomain = toSignal(this.domainEffect.fetchDomain(), { initialValue: null });
  protected readonly domainData = computed<Domain>(() => this.isAdmin() && this.currentDomain());
  protected readonly showHelpButton = computed(() => showHelpButton(this.domainData()));

  protected readonly initialSettingsEffect = effect(() => {
    const $settings = this.initialSettings();

    this.disabledSecretKey.set(Boolean($settings?.secretKey));
    this.updateReadonly($settings);
  });

  get storageTypeCtrl(): FormControl<StorageType> {
    return this.formGroup().controls[FormKey.StorageType];
  }

  get secretKeyCtrl(): FormControl<string> {
    return this.formGroup().controls[FormKey.SecretKey];
  }

  ngOnInit(): void {
    this.initStorageTypeChanges();
  }

  private initStorageTypeChanges(): void {
    this.storageTypeChanges = toSignal(this.storageTypeCtrl.valueChanges.pipe(distinctUntilChanged()), {
      initialValue: this.storageTypeCtrl.value,
      injector: this.injector
    });
  }

  private updateReadonly(settings: BackupDTabFormStructure): void {
    if (!settings) return this.readonly.set(false);

    const {
      [FormKey.AccessKey]: accessKey,
      [FormKey.BucketName]: bucketName,
      [FormKey.Name]: displayName,
      [FormKey.Region]: region,
      [FormKey.RootFolder]: rootFolder
    } = settings || {};
    const hasRegions = this.uiDataService.isAvailableRegion(this.storageTypeCtrl.value);
    const baseFields = [accessKey, bucketName, displayName, rootFolder];
    const requiredFields = hasRegions ? [...baseFields, region].filter(Boolean) : baseFields;
    const allRequiredFilled = requiredFields.every(Boolean);

    this.readonly.set(allRequiredFilled);
  }

  handleToggleDisabledSecretKey(): void {
    this.disabledSecretKey.update((value) => !value);

    const $disabledSecretKey = this.disabledSecretKey();
    const secretKey = this.secretKeyCtrl.value;

    if (!$disabledSecretKey && secretKey === SECRET_KEY_MASK) {
      this.secretKeyCtrl.setValue('');
    }

    if ($disabledSecretKey && secretKey === '' && this.initialSettings()?.[FormKey.SecretKey]) {
      this.secretKeyCtrl.setValue(SECRET_KEY_MASK);
    }
  }
}
